import * as React from "react";
import {useEffect, useState} from "react";
import {useSelector} from "react-redux";
import {has} from "lodash";
import BackToIndexButton from "../components/BackToIndexButton";
import cards from "../components/Cards";
import {
  ArticleContainer,
  FooterContainer,
  Divider,
  HeaderContainer,
} from "../components/Containers";
import LoadMoreArticlesButton from "../components/LoadMoreArticlesButton";
import MainContainer from "../components/MainContainer";
import {ARTICLE_LOADING, ArticleContext} from "../components/article/ArticleCard";
import ArticleIntroduction from "../components/article/ArticleIntroduction";
import ArticleTitle from "../components/article/ArticleTitle";
import {templates} from "../config";
import {LAYOUT_CONSTANTS} from "../const";
import {ArticleEntity} from "../contract/article";
import {TopicEntity} from "../contract/topic";
import useCurrentRoute from "../hooks/useCurrentRoute";
import useMedia from "../hooks/useMedia";
import {INITIAL_ROUTE} from "../hooks/useRoutes";
import useTemplate from "../hooks/useTemplate";
import {RootState} from "../redux/store";

const {FORM_ROW_PADDING}: {
  FORM_ROW_PADDING: React.ReactText|string,
} = LAYOUT_CONSTANTS;

interface Props {
  slug?: string;
  path?: string;
}

const NewsTopicContent: React.FC<Props> = ({
  slug,
}): React.ReactElement => {
  const {onSmallMobile, onMobile} = useMedia();

  const {topic} = useSelector((state: RootState) => {
    const topics = Object.values(state.entities.topics.byId);
    const topic = (topics || []).find((node: TopicEntity) => node.slug === slug);

    return {topic};
  });

  return (
    <ArticleContainer direction="column" className={"f-5 theme--primary-font-color"} style={{position: 'relative'}}>
      <ArticleContext.Provider value={{
        content: {
          ...ARTICLE_LOADING,
          ...(topic ?? {}),
          loading: false,
          visible: !!topic?.content
        }
      }}>
        <ArticleTitle
          size={onSmallMobile ? 3 : onMobile ? 2 : 1}
          className={`fw-700 theme--primary-color`}
          style={{
            minHeight: !topic?.content ? '100px' : 'unset',
            position: 'relative',
            margin: !topic?.content ? `${FORM_ROW_PADDING} 0` : onMobile ? `0.5rem 0` : `${FORM_ROW_PADDING} 0`
          }}
        />
        <ArticleIntroduction
          compact={false}
          size={5}
          className={"theme--primary-font-color"}
          style={{
            minHeight: !topic?.content ? '250px' : 0,
            position: 'relative',
            marginBottom: !topic?.content ? `calc(${FORM_ROW_PADDING}/2)` : 0
          }}
        >
          <div dangerouslySetInnerHTML={
            {__html: topic?.content}
          }/>
        </ArticleIntroduction>
      </ArticleContext.Provider>
    </ArticleContainer>
  )
};

const NewsTopic: React.FC<Props> = (): React.ReactElement => {
  const pathname = useCurrentRoute();
  const {onMobile} = useMedia();
  const [slug, setSlug] = useState<string>(null);

  const {articles_visible, base, breakpoints, theme, topic} = useSelector((state: RootState) => {
    const {config, loading, page_size, routes, theme} = state.application;
    const articles = Object.values(state.entities.articles.byId);
    const topics = Object.values(state.entities.topics.byId);
    const topic = !(loading || []).length && (topics || []).find((node: TopicEntity) => node.slug === slug);
    const route = has(routes, pathname) ? routes[pathname] : INITIAL_ROUTE;
    const {articles_visible} = route;
    const {breakpoints} = templates.find(({id}: {id: string}) => id === 'headlines');
    const id = topic?.id;

    return {
      articles: id ? (articles || []).filter(
        (node: ArticleEntity) => node.topics.find((topic: string) => topic === String(id))
      ) : [],
      articles_visible,
      base: config.base,
      breakpoints,
      page_size,
      theme,
      topic,
    }
  });

  useEffect(() => {
    if (!base || !pathname) return;

    try {
      setSlug(pathname.split(base)[1].slice(1).replace(/\/$/, ''));
    } catch (e) {
      console.error(`${pathname} could not be resolved!`);
      setSlug(null);
    }
  }, [base, pathname]);

  const {sections, containerStyles, LAYOUT_CONSTANTS} = useTemplate(breakpoints);

  const {CONTENT_MAX_WIDTH, FORM_ROW_PADDING, GUTTER_SIZE}: {
    FORM_ROW_PADDING: string;
    GUTTER_SIZE: number;
    CONTENT_MAX_WIDTH: number;
  } = LAYOUT_CONSTANTS;

  return (
    <MainContainer role="main" theme={theme} style={containerStyles}>
      <HeaderContainer style={{justifyContent: 'center'}}>
        <BackToIndexButton />
      </HeaderContainer>

      <NewsTopicContent path={pathname} slug={slug} />

      {!onMobile && (
        <Divider style={{margin: `${FORM_ROW_PADDING} 0 ${GUTTER_SIZE}px 0`}} />
      )}

      {(sections || []).map(({template, columns, entries}, index = 0) => {
        if (!has(cards, template)) return;

        return entries.map((entry: ArticleEntity, i: number) => (
          React.createElement(
            cards[template],
            {
              content: entry || {},
              final: (i === entries.length - 1),
              index: i + 1,
              columns: entries.length < columns ? entries.length : columns,
              maxWidth: CONTENT_MAX_WIDTH,
              priority: -1, // sections.length - index, //
              loading: !(articles_visible || []).find(id => id === String(entry?.id)),
              key: entry?.slug || `${index}-${i}`,
            }
          )
        ));
      })}

      <FooterContainer
        style={{
          justifyContent: 'center',
        }}
      >
        <LoadMoreArticlesButton query={{topic: String(topic?.id)}} />
      </FooterContainer>
    </MainContainer>
  );
};

export default NewsTopic;
